import { JSXElementConstructor, ReactElement, SyntheticEvent } from 'react'

import type { AlignType } from '@rc-component/trigger/lib/interface'
import { Alert, Typography } from 'antd'
import { TooltipPlacement } from 'antd/es/tooltip'

import { Flex } from 'components/core'
import LockIcon from 'components/Icons/LockIcon/LockIconSvg'
import SlashCircleIcon from 'components/Icons/SlashCircleIcon/SlashCircleIconSvg'
import CustomPopover from 'components/Popover/Popover'
import { TableColumnTooltipsMetadata } from 'configuration/fieldCreators/localTypes'

import * as styles from './styles.scss'

type MetricTooltipAlertType = 'disallowed' | 'locked'

export interface FieldTooltipProps {
    title: ReactElement | string
    showSubtext?: boolean
    subtext?: string | null | ReactElement
    body?: ReactElement | string
    children: ReactElement | string
    placement?: TooltipPlacement
    align?: AlignType
    alertMessage?: ReactElement | string | null
    alertType?: MetricTooltipAlertType
    zIndex?: number
    callToAction?:
        | (() => ReactElement<any, string | JSXElementConstructor<any>>)
        | null
    metadata?: TableColumnTooltipsMetadata
}
export default function FieldTooltip({
    title,
    body,
    children,
    placement,
    align,
    alertMessage,
    alertType,
    zIndex,
    showSubtext = true,
    subtext = null,
    callToAction = null,
    metadata: _unused_metadata,
}: FieldTooltipProps): ReactElement {
    let alertIcon: ReactElement | undefined
    let subTextIcon: ReactElement | undefined

    if (alertType === 'disallowed') {
        alertIcon = (
            <SlashCircleIcon
                style={{
                    fontSize: 18,
                    opacity: 0.75,
                }}
            />
        )
    } else if (alertType === 'locked') {
        alertIcon = (
            <LockIcon
                style={{
                    fontSize: 18,
                    opacity: 0.5,
                }}
            />
        )
        subTextIcon = (
            <LockIcon
                style={{
                    fontSize: 14,
                    opacity: 0.5,
                }}
            />
        )
    }

    const titleContent = (
        <Flex
            direction="column"
            alignItems="normal"
            gap={4}
            style={{
                maxWidth: 400,
                padding: '14px 16px 18px',
                boxSizing: 'border-box',
            }}
            onClick={(e: SyntheticEvent) => {
                e.stopPropagation()
            }}
        >
            {alertMessage && (
                <Alert
                    message={
                        <div>
                            <Typography.Paragraph
                                style={{
                                    color: 'rgba(0, 0, 0, 0.75)',
                                    fontSize: '12.6px',
                                    lineHeight: '1.4',
                                    margin: 0,
                                    fontWeight: '400',
                                    display: 'flex',
                                    alignItems: 'center', // Align items vertically
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {alertIcon && (
                                        <div
                                            style={{
                                                marginRight: '0.7em',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {alertIcon}
                                        </div>
                                    )}
                                    <span>{alertMessage}</span>
                                </div>
                            </Typography.Paragraph>
                            {callToAction && callToAction()}
                        </div>
                    }
                    showIcon={false}
                    style={{
                        borderTop: 'none',
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderBottom: '1px solid #d9d9d9',
                        borderRadius: '6px 6px 0 0',
                        backgroundColor: '#FAFAFA',
                        margin: '-14px -16px 16px',
                    }}
                />
            )}
            {title}
            {body}
        </Flex>
    )
    return (
        <CustomPopover
            title={titleContent}
            rootClassName={styles['field-tooltip']}
            placement={placement}
            zIndex={zIndex}
            align={align}
        >
            <div>
                <div>{children ?? '-'}</div>
                {showSubtext && subtext && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <span className={styles['column-tooltip']}>
                            {subtext}
                        </span>
                        {subTextIcon}
                    </div>
                )}
            </div>
        </CustomPopover>
    )
}
